import { uploadMeetingPhoto } from "../../api/Meetings/meetingSessionsApi";

/** Cola fuera del árbol React: la subida sigue si sales de la reunión. */
const bySession = new Map();
const listeners = new Set();

const notify = () => {
  listeners.forEach((fn) => {
    try {
      fn();
    } catch {
      /* ignore */
    }
  });
};

export const subscribeMeetingMediaUploads = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

export const listSessionMediaUploads = (sessionId) => {
  const map = bySession.get(String(sessionId));
  return map ? [...map.values()] : [];
};

export const countUploadingMedia = (sessionId) =>
  listSessionMediaUploads(sessionId).filter((e) => e.status === "uploading")
    .length;

export const waitForSessionMediaUploads = (sessionId) => {
  const pending = listSessionMediaUploads(sessionId).filter(
    (e) => e.status === "uploading" && e.promise,
  );
  return Promise.allSettled(pending.map((e) => e.promise));
};

/**
 * Fusiona fotos del GET con subidas en curso / recién terminadas
 * (por si el GET llega antes de que el cliente tenga la respuesta).
 */
export const mergePhotosWithMediaUploads = (photos, sessionId) => {
  const entries = listSessionMediaUploads(sessionId);
  const byId = new Map(
    (photos || [])
      .filter((p) => p && !p.pendingUpload)
      .map((p) => [Number(p.id), p]),
  );
  const pendingLocals = [];
  for (const entry of entries) {
    if (entry.status === "done" && entry.photo) {
      const idNum = Number(entry.photo.id);
      if (Number.isFinite(idNum) && !byId.has(idNum)) {
        byId.set(idNum, entry.photo);
      }
    } else if (entry.status === "uploading" && entry.pendingPhoto) {
      pendingLocals.push(entry.pendingPhoto);
    }
  }
  const sorted = [...byId.values()].sort(
    (a, b) => Number(a.offsetMs) - Number(b.offsetMs),
  );
  return [...sorted, ...pendingLocals];
};

const scheduleCleanup = (sessionId, localId, delayMs) => {
  window.setTimeout(() => {
    const map = bySession.get(String(sessionId));
    if (!map) return;
    const entry = map.get(localId);
    if (!entry || entry.status === "uploading") return;
    if (entry.pendingPhoto?.fileUrl?.startsWith?.("blob:")) {
      try {
        URL.revokeObjectURL(entry.pendingPhoto.fileUrl);
      } catch {
        /* ignore */
      }
    }
    map.delete(localId);
    if (map.size === 0) bySession.delete(String(sessionId));
    notify();
  }, delayMs);
};

export const enqueueMeetingPhotoUpload = ({
  sessionId,
  file,
  pendingPhoto,
  offsetMs,
  capturedAt,
}) => {
  const sid = String(sessionId);
  if (!bySession.has(sid)) bySession.set(sid, new Map());
  const map = bySession.get(sid);
  const entry = {
    localId: String(pendingPhoto.id),
    status: "uploading",
    pendingPhoto,
    photo: null,
    error: null,
    promise: null,
  };
  map.set(entry.localId, entry);
  notify();

  entry.promise = uploadMeetingPhoto(sessionId, file, {
    offsetMs,
    capturedAt,
  })
    .then((photo) => {
      if (String(entry.pendingPhoto?.fileUrl || "").startsWith("blob:")) {
        try {
          URL.revokeObjectURL(entry.pendingPhoto.fileUrl);
        } catch {
          /* ignore */
        }
      }
      entry.status = "done";
      entry.photo = photo;
      notify();
      scheduleCleanup(sid, entry.localId, 120000);
      return photo;
    })
    .catch((err) => {
      entry.status = "error";
      entry.error = err;
      notify();
      scheduleCleanup(sid, entry.localId, 15000);
      throw err;
    });

  return entry;
};
